import {zipObject} from 'lodash';
import React, {FC, Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {ConversationInquiryModel} from '../../../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {TrackDataEvent} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {RelatedKnowledgeReference} from '../../../../../models/api/relatedKnowledgeModel';
import {SimilarInquiry} from '../../../../../models/api/similarInquiriesModel';
import {SynopsisCollapsedGroup} from './synopsisCollapsedGroup';
import {SynopsisCommentPreview} from './synopsisCommentPreview';
import {SynopsisFeedbackModal} from './synopsisFeedbackModal';
import {
  buildSynopsisItemGroups,
  buildSynopsisItems,
  SynopsisItem,
  SynopsisItemGroup,
  SynopsisItemTypesEnum,
} from './synopsisHelpers';
import {SynopsisLinkedConversation} from './synopsisLinkedConversation';
import {SynopsisMessagePreview} from './synopsisMessagePreview';
import {SynopsisOneLiner} from './synopsisOneLiner';
import {SynopsisPinnedCommentContainer} from './synopsisPinnedCommentContainer';
import {useSynopsisViewerContext} from './synopsisViewerContext';

/*
 * Props.
 */

export interface SynopsisViewerProps {
  conversation: ConversationModel;
  pinnedActivities: ReadonlyArray<ActivityModel>;
  inquiries: ReadonlyArray<ConversationInquiryModel>;
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>>;
  relatedKnowledge: Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>;
  panelId: ViewerPanelId;
}

/*
 * Styles.
 */

export const StyledSynopsisContentDiv = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 16px;
  overflow-y: auto;
  color: ${(p) => p.theme.text};
  line-height: 1.3;
  height: 100%;
  width: 100%;
`;

const StyledTimelineDiv = styled.div<{$isFirst?: boolean; $isFollowUp?: boolean}>`
  position: relative;

  ${(p) => addVerticalLine(p)};

  ${(p) => p.$isFollowUp && 'margin-top: -12px'};
`;

function addVerticalLine(props: {$isFirst?: boolean; $isFollowUp?: boolean}) {
  if (props.$isFirst || props.$isFollowUp) {
    return '';
  }

  return css`
    &:before {
      content: '';
      position: absolute;
      height: 16px;
      top: -16px;
      left: 20px;
      border: 1px solid ${(p) => p.theme.message.readCommentBackground};
    }
  `;
}

const StyledTimelinePill = styled.div`
  position: absolute;
  top: 6px;
  left: 17px;
  width: 8px;
  height: 8px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.greys.shade40};
`;

const StyledSynopsisCollapsedGroup = styled(SynopsisCollapsedGroup)`
  margin-left: 20px;
  padding-left: 16px;
`;

/*
 * Component.
 */

export const SynopsisViewer: FC<SynopsisViewerProps> = ({
  conversation,
  pinnedActivities,
  inquiries,
  similarInquiries,
  relatedKnowledge,
  panelId,
}) => {
  const {onTrackDataEvent} = useSynopsisViewerContext();
  const [synopsisGroupItems, onExpandGroup] = useSynopsisGroupItems(
    conversation.id,
    inquiries,
    pinnedActivities,
    similarInquiries,
    relatedKnowledge,
    onTrackDataEvent,
  );

  return (
    <>
      <StyledSynopsisContentDiv>
        <SynopsisOneLiner conversation={conversation} />
        {synopsisGroupItems.map((group, index) => (
          <Fragment key={group.id}>
            {renderSynopsisGroupItem(group, index === 0, panelId, onExpandGroup)}
          </Fragment>
        ))}
      </StyledSynopsisContentDiv>
      <SynopsisFeedbackModal />
    </>
  );
};

/*
 * Helpers.
 */

function useSynopsisGroupItems(
  conversationId: number,
  inquiries: ReadonlyArray<ConversationInquiryModel>,
  pinnedActivities: ReadonlyArray<ActivityModel>,
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>>,
  relatedKnowledge: Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>,
  onTrackDataEvent: TrackDataEvent,
): readonly [ReadonlyArray<SynopsisItemGroup>, (groupId: string) => void] {
  const [synopsisItems, setSynopsisItems] = useState(
    buildSynopsisItems(inquiries, pinnedActivities, similarInquiries, relatedKnowledge),
  );

  // Store collapsed status independently so it doesn't get erased when synopsis updates.
  const [collapsedItems, setCollapsedItems] = useState<Record<string, boolean>>({});

  const synopsisGroupItems = useMemo(
    () => buildSynopsisItemGroups(synopsisItems, collapsedItems),
    [collapsedItems, synopsisItems],
  );

  const onExpandGroup = useCallback(
    (groupId: string) => {
      const group = synopsisGroupItems.find(({id}) => id === groupId);
      if (!group) {
        return;
      }

      const itemIds = group.items.map((item) => item.id);

      setCollapsedItems({
        ...collapsedItems,
        ...zipObject(
          itemIds,
          itemIds.map(() => false),
        ),
      });

      const firstItem = group.items[0];

      if (firstItem) {
        onTrackDataEvent(DataEventActionsEnum.EXPAND, ViewerDataEventObject.SYNOPSIS_ITEM, {
          activity_id: firstItem.id,
          type: firstItem.type,
        });
      }
    },
    [collapsedItems, onTrackDataEvent, synopsisGroupItems],
  );

  // Re-render when new conversation changes
  useEffect(() => {
    setSynopsisItems(buildSynopsisItems(inquiries, pinnedActivities, similarInquiries, relatedKnowledge));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, pinnedActivities, relatedKnowledge, similarInquiries]);

  return [synopsisGroupItems, onExpandGroup];
}

function renderSynopsisGroupItem(
  group: SynopsisItemGroup,
  isFirstGroup: boolean,
  panelId: ViewerPanelId,
  onExpandGroup: (groupId: string) => void,
) {
  if (group.isCollapsed) {
    return (
      <StyledTimelineDiv $isFirst={isFirstGroup}>
        <StyledTimelinePill />
        <StyledSynopsisCollapsedGroup items={group.items} onExpand={() => onExpandGroup(group.id)} />
      </StyledTimelineDiv>
    );
  }

  return (
    <>
      {group.items.map((item, index) => (
        <StyledTimelineDiv key={item.id} $isFirst={isFirstGroup && index === 0} $isFollowUp={index > 0}>
          {renderSynopsisItem(item, index > 0, panelId)}
        </StyledTimelineDiv>
      ))}
    </>
  );
}

function renderSynopsisItem(item: SynopsisItem, isFollowUp: boolean, panelId: ViewerPanelId) {
  const {type} = item;
  switch (type) {
    case SynopsisItemTypesEnum.MESSAGE:
      return <SynopsisMessagePreview item={item} isFollowUp={isFollowUp} panelId={panelId} />;

    case SynopsisItemTypesEnum.COMMENT:
      return <SynopsisCommentPreview item={item} isFollowUp={isFollowUp} panelId={panelId} />;

    case SynopsisItemTypesEnum.PINNED_COMMENT:
      return <SynopsisPinnedCommentContainer item={item} panelId={panelId} />;

    case SynopsisItemTypesEnum.LINKED_CONVERSATION:
      return <SynopsisLinkedConversation item={item} panelId={panelId} />;

    default:
      throw new UnreachableError(type);
  }
}
